import { Component, computed, HostListener, input, OnDestroy, output, signal } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { FormsModule } from '@angular/forms';
import { getConfidenceRating, WorkflowItem, WorkflowBadgeStatus, WorkflowStatus } from '#models';
import { exceptionTypeLabel } from '../exception-type-label';
import { reportDate, watCalendarDate } from '../report-date';
import { isProductionDeviationItem } from '../detail/mappers/production-deviation-workflow.mapper';
import { WorkflowInboxFilterModalComponent, WorkflowInboxFilterState } from './workflow-inbox-filter-modal.component';

export type WorkflowSortOption =
  | 'newest-date'
  | 'oldest-date'
  | 'newest-completed'
  | 'oldest-completed'
  | 'low-priority'
  | 'high-priority';

type BadgeTooltipKind = 'priority' | 'case-type' | 'workflow' | 'context' | 'run-for-date';

export interface WorkflowSidebarFilters {
  searchText: string;
  asset: string | null;
  timeline: string;
  date: string | null;
  runForDate?: string | null;
  exceptionType: string | null;
  status: string | null;
  priority: string | null;
  sortOption: WorkflowSortOption;
  workflowStatus?: WorkflowStatus | null;
  myPendingActions?: boolean;
}

@Component({
  selector: 'app-workflow-sidebar',
  imports: [FormsModule, WorkflowInboxFilterModalComponent, OverlayModule, CdkScrollable],
  templateUrl: './workflow-sidebar.component.html',
  styleUrl: './workflow-sidebar.component.scss',
})
export class WorkflowSidebarComponent implements OnDestroy {
  readonly priorityTooltipItemId = signal<string | null>(null);
  readonly badgeTooltipKind = signal<BadgeTooltipKind>('priority');
  private _tooltipHideTimer?: ReturnType<typeof setTimeout>;
  items = input.required<WorkflowItem[]>();
  allItems = input.required<WorkflowItem[]>();
  selectedItem = input<WorkflowItem | null>(null);
  assets = input.required<string[]>();
  selectedAsset = input<string | null>(null);
  selectedSearchText = input<string>('');
  selectedTimeline = input<string>('all');
  selectedDate = input<string | null>(null);
  selectedRunForDate = input<string | null>(null);
  selectedExceptionType = input<string | null>(null);
  selectedStatus = input<string | null>(null);
  selectedWorkflowStatus = input<WorkflowStatus | null>(null);
  selectedMyPendingActions = input(false);
  selectedPriority = input<string | null>(null);
  selectedSortOption = input<WorkflowSortOption>('newest-date');
  casesLoading = input<boolean>(false);
  resultsSettled = input(true);

  itemSelected = output<WorkflowItem>();
  assetChanged    = output<string | null>();
  timelineChanged = output<string>();
  filtersChanged = output<WorkflowSidebarFilters>();

  filterModalOpen = false;
  sortMenuOpen = false;
  private _alphaNumericCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  readonly sortOptions: { value: WorkflowSortOption; label: string }[] = [
    { value: 'newest-date', label: 'Newest run for date' },
    { value: 'oldest-date', label: 'Oldest run for date' },
    { value: 'newest-completed', label: 'Newest run completed date' },
    { value: 'oldest-completed', label: 'Oldest run completed date' },
    { value: 'low-priority', label: 'Low priority' },
    { value: 'high-priority', label: 'High priority' },
  ];

  displayItems = computed(() => this.items());
  newCount = computed(() => this.displayItems().filter(item =>
    item.badge === WorkflowBadgeStatus.New || item.badge === WorkflowBadgeStatus.Proposed).length);
  countLabel = computed(() => {
    if (this.selectedMyPendingActions()) return `${this.displayItems().length} pending for me`;
    const status = this.selectedWorkflowStatus();
    return status
      ? `${this.displayItems().length} ${this.workflowStatusLabel({ workflowStatus: status }).toLowerCase()}`
      : `${this.newCount()} new`;
  });
  hasAdvancedFilters = computed(() =>
    this.selectedSearchText().trim().length > 0
    || this.selectedTimeline() !== 'all'
    || !!this.selectedDate()
    || !!this.selectedRunForDate()
    || !!this.selectedExceptionType()
    || !!this.selectedStatus()
    || !!this.selectedWorkflowStatus()
    || this.selectedMyPendingActions()
    || !!this.selectedPriority()
  );
  exceptionTypeOptions = computed(() => this._uniqueSorted(this.allItems().map(item => this.getTypeLabel(item))));
  priorityOptions = computed(() => ['low', 'medium', 'high']);
  readonly WorkflowBadgeStatus = WorkflowBadgeStatus;

  priorityTooltip(item: WorkflowItem): string {
    const heading = this.priorityTooltipHeading(item);
    return heading ? `${heading} ${this.priorityTooltipDescription(item)}` : 'Priority is unavailable.';
  }

  priorityTooltipHeading(item: WorkflowItem): string {
    const level = this.priorityLevel(item);
    return level ? `${level === 'medium' ? 'Medium' : this.getPriorityLabel(item)} priority:` : '';
  }

  priorityTooltipDescription(item: WorkflowItem): string {
    const level = this.priorityLevel(item);
    if (!level) return 'Priority is unavailable.';
    if (this.isWellException(item)) {
      const scopes = {
        low: 'a single well investigation target',
        medium: 'a single jacket target, or equipment whose member wells all map to the same single jacket',
        high: 'multiple investigation targets, a facility target, or equipment spanning jackets or with missing or ambiguous jacket mappings',
      };
      return `the Well Exception agent assigns this level for ${scopes[level]}. Priority reflects investigation scope, not confidence or confirmed production impact.`;
    }
    if (this.isProductDeviation(item)) {
      return 'assigned during web API ingestion from the supplied priority, defaulting to Low if it is missing or unrecognized. The current Product Deviation agent output does not supply priority. Priority is not derived from confidence.';
    }
    return 'as recorded by the source.';
  }

  showPriorityTooltip(item: WorkflowItem): void {
    this.showBadgeTooltip(item, 'priority');
  }

  showBadgeTooltip(item: WorkflowItem, kind: BadgeTooltipKind): void {
    clearTimeout(this._tooltipHideTimer);
    this.badgeTooltipKind.set(kind);
    this.priorityTooltipItemId.set(kind !== 'priority' || this.priorityLevel(item) ? item.id : null);
  }

  deferHidePriorityTooltip(): void {
    clearTimeout(this._tooltipHideTimer);
    this._tooltipHideTimer = setTimeout(() => this.hidePriorityTooltip(), 150);
  }

  onPriorityTooltipDetached(item: WorkflowItem, kind: BadgeTooltipKind = 'priority'): void {
    if (this.priorityTooltipItemId() === item.id && this.badgeTooltipKind() === kind) this.hidePriorityTooltip();
  }

  @HostListener('document:keydown.escape')
  hidePriorityTooltip(): void {
    clearTimeout(this._tooltipHideTimer);
    this.priorityTooltipItemId.set(null);
  }

  ngOnDestroy(): void {
    clearTimeout(this._tooltipHideTimer);
  }

  onItemClick(item: WorkflowItem): void {
    this.itemSelected.emit(item);
  }

  onAssetChange(asset: string | null): void {
    this.assetChanged.emit(asset);
  }

  onResetFilters(): void {
    this.filterModalOpen = false;
    this.sortMenuOpen = false;
    this.filtersChanged.emit({
      searchText: '',
      asset: this._defaultAssetSelection(),
      timeline: 'all',
      date: null,
      runForDate: null,
      exceptionType: null,
      status: null,
      priority: null,
      sortOption: 'newest-date',
      workflowStatus: null,
      myPendingActions: false,
    });
  }

  onOpenFilterModal(event?: MouseEvent): void {
    event?.stopPropagation();
    this.sortMenuOpen = false;
    this.filterModalOpen = !this.filterModalOpen;
  }

  onFilterApplied(state: WorkflowInboxFilterState): void {
    this.filtersChanged.emit({
      searchText: state.searchText.trim(),
      asset: state.asset,
      timeline: this._normalizeTimeline(state.timeline),
      date: state.date,
      runForDate: state.runForDate ?? null,
      exceptionType: state.exceptionType,
      status: this._normalizeStatus(state.status),
      workflowStatus: state.workflowStatus ?? null,
      myPendingActions: state.myPendingActions ?? false,
      priority: state.priority,
      sortOption: this.selectedSortOption(),
    });
    this.filterModalOpen = false;
  }

  onFilterCancelled(): void {
    this.filterModalOpen = false;
  }

  onToggleSortOrder(event?: MouseEvent): void {
    event?.stopPropagation();
    this.filterModalOpen = false;
    this.sortMenuOpen = !this.sortMenuOpen;
  }

  onSortOptionSelected(option: WorkflowSortOption): void {
    this.filtersChanged.emit({
      searchText: this.selectedSearchText().trim(),
      asset: this.selectedAsset(),
      timeline: this._normalizeTimeline(this.selectedTimeline()),
      date: this.selectedDate(),
      runForDate: this.selectedRunForDate(),
      exceptionType: this.selectedExceptionType(),
      status: this._normalizeStatus(this.selectedStatus()),
      workflowStatus: this.selectedWorkflowStatus(),
      myPendingActions: this.selectedMyPendingActions(),
      priority: this.selectedPriority(),
      sortOption: option,
    });
    this.sortMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.inbox-head__filter-wrap')) {
      this.filterModalOpen = false;
    }

    if (!target?.closest('.inbox-head__sort-wrap')) {
      this.sortMenuOpen = false;
    }
  }

  isSelected(item: WorkflowItem): boolean {
    const selected = this.selectedItem();
    return item.id === selected?.id
      || (!!selected?.engineCaseId && item.linkedEngineCaseId === selected.engineCaseId);
  }

  getBadgeClass(badge: WorkflowBadgeStatus): string {
    const map: Record<WorkflowBadgeStatus, string> = {
      [WorkflowBadgeStatus.New]:              'badge-new',
      [WorkflowBadgeStatus.AwaitingDecision]: 'badge-waiting',
      [WorkflowBadgeStatus.Closed]:           'badge-closed',
      [WorkflowBadgeStatus.Proposed]:         'badge-new',
    };
    return map[badge];
  }

  getBadgeLabel(badge: WorkflowBadgeStatus): string {
    const map: Record<WorkflowBadgeStatus, string> = {
      [WorkflowBadgeStatus.New]:              'New',
      [WorkflowBadgeStatus.AwaitingDecision]: 'Awaiting Decision',
      [WorkflowBadgeStatus.Closed]:           'Closed',
      [WorkflowBadgeStatus.Proposed]:         'Agent finding',
    };
    return map[badge];
  }

  isWellException(item: WorkflowItem): boolean {
    return item.source === 'uc2-case'
      || item.persistedCase?.useCase?.toUpperCase() === 'UC2'
      || item.persistedCase?.caseType === 'well-exception'
      || item.identity?.useCase?.toUpperCase() === 'UC2';
  }

  isProductDeviation(item: WorkflowItem): boolean {
    return !this.isWellException(item) && isProductionDeviationItem(item);
  }

  workflowIndicator(item: WorkflowItem): 'new' | 'claimed' | 'done' | null {
    if (!(item.linkedEngineCaseId || item.engineCaseId || item.workflowCaseId)) return null;
    if (item.workflowStatus === 'in-progress') return 'claimed';
    if (item.workflowStatus === 'new') return 'new';
    if (item.workflowStatus === 'done') return 'done';
    return null;
  }

  workflowTooltip(item: WorkflowItem): string {
    const detail = this.workflowTooltipDetail(item);
    return this.workflowStatusLabel(item) + (detail ? ` - ${detail}` : '');
  }

  workflowTooltipDetail(item: WorkflowItem): string | null {
    return item.workflowStatus === 'in-progress'
      ? item.workflowStepTitle || 'Current step unavailable'
      : null;
  }

  workflowStatusLabel(item: Pick<WorkflowItem, 'workflowStatus'>): string {
    return item.workflowStatus === 'new' ? 'Open'
      : item.workflowStatus === 'in-progress' ? 'In progress'
        : item.workflowStatus === 'done' ? 'Closed' : 'Workflow';
  }

  getReportDate(item: WorkflowItem): string {
    const date = this.isWellException(item)
      ? watCalendarDate(item.persistedCase?.eventDateUtc)
      : reportDate(item);
    return date ?? 'Not recorded';
  }

  getReportDateLabel(item: WorkflowItem): string {
    return this.isWellException(item) ? 'First observed' : 'Run for date';
  }

  getContextLine(item: WorkflowItem): string {
    return item.identity?.supplementary?.trim() || item.asset || item.description;
  }

  getTypeLabel(item: WorkflowItem): string {
    return exceptionTypeLabel(item);
  }

  getConfidenceLabel(item: WorkflowItem): string {
    const confidence = this.confidenceScore(item);
    if (confidence === null) {
      return 'Unknown';
    }

    const rating = getConfidenceRating(confidence);
    return `${rating} · ${Math.round(confidence)}%`;
  }

  confidenceScore(item: WorkflowItem): number | null {
    const score = item.confidenceScore !== undefined ? item.confidenceScore : item.identity?.confidenceLevel;
    return typeof score === 'number' && Number.isFinite(score) && score >= 0 && score <= 100
      && (item.confidenceScore !== undefined || score > 0) ? score : null;
  }

  getConfidencePercent(item: WorkflowItem): number {
    const confidence = item.identity?.confidenceLevel ?? 0;
    if (confidence <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, Math.round(confidence)));
  }

  priorityLevel(item: WorkflowItem): 'high' | 'medium' | 'low' | null {
    const normalized = this._normalized(item.priority);
    if (normalized === 'high') {
      return 'high';
    }
    if (normalized === 'medium' || normalized === 'med') {
      return 'medium';
    }
    if (normalized === 'low') {
      return 'low';
    }
    return null;
  }

  getPriorityLabel(item: WorkflowItem): string | null {
    const level = this.priorityLevel(item);
    if (level === 'high') {
      return 'High';
    }
    if (level === 'medium') {
      return 'Med';
    }
    if (level === 'low') {
      return 'Low';
    }
    return null;
  }

  private _normalized(value?: string | null): string {
    return value?.trim().toLowerCase() ?? '';
  }

  private _normalizeStatus(value: string | null | undefined): string | null {
    const normalized = this._normalized(value);
    if (!normalized) {
      return null;
    }

    if (normalized === 'new' || normalized === 'awaiting decision' || normalized === 'closed') {
      return normalized;
    }

    return null;
  }

  private _uniqueSorted(values: string[]): string[] {
    const unique = Array.from(new Set(values.map(value => value.trim()).filter(value => value.length > 0)));
    return unique.sort((a, b) => this._alphaNumericCollator.compare(a, b));
  }

  private _defaultAssetSelection(): string | null {
    const options = this.assets();
    return options.length > 1 ? null : (options[0] ?? null);
  }

  private _normalizeTimeline(value: string | null | undefined): string {
    const normalized = value?.trim().toLowerCase() ?? 'all';
    return normalized === '1w' || normalized === '2w' || normalized === '1m' || normalized === 'all'
      ? normalized
      : 'all';
  }
}
